"use client";

import * as React from "react";
import { PieChart, Pie, Cell, Sector, ResponsiveContainer } from "recharts";
import { formatUsdCompact } from "@/utils/format-price";

type PieDatum = {
  name: string;
  liquidity: number;
  percentage: number;
};

type LiquidityPieProps = {
  data: PieDatum[];
  colors: readonly string[];
  isLoading?: boolean;
};

function ActiveShape(props: {
  cx: number;
  cy: number;
  innerRadius: number;
  outerRadius: number;
  startAngle: number;
  endAngle: number;
  fill: string;
}) {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } = props;
  return (
    <g>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius - 4}
        outerRadius={outerRadius + 12}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
        stroke={fill}
        strokeWidth={1.5}
        style={{ filter: `drop-shadow(0 0 14px ${fill}) brightness(1.15)` }}
      />
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={outerRadius + 14}
        outerRadius={outerRadius + 18}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
        opacity={0.4}
      />
    </g>
  );
}

export function LiquidityPie({ data, colors, isLoading }: LiquidityPieProps) {
  const [active, setActive] = React.useState(0);
  const activeItem = data[active];

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-surface-500 border-t-hex-400" />
      </div>
    );
  }

  if (!data.length) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted">
        No pools for this filter
      </div>
    );
  }

  return (
    <div className="relative h-full w-full overflow-visible px-2">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart margin={{ top: 12, right: 12, bottom: 12, left: 12 }}>
          <defs>
            {colors.map((c, i) => (
              <linearGradient key={i} id={`pie-grad-${i}`} x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor={c} stopOpacity={1} />
                <stop offset="55%" stopColor={c} stopOpacity={0.75} />
                <stop offset="100%" stopColor="#0a0a12" stopOpacity={0.35} />
              </linearGradient>
            ))}
            <filter id="pie-glow">
              <feGaussianBlur stdDeviation="2" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
          <Pie
            cx="50%"
            cy="50%"
            data={data}
            dataKey="liquidity"
            innerRadius="54%"
            outerRadius="76%"
            stroke="rgba(255,255,255,0.06)"
            strokeWidth={1}
            paddingAngle={3}
            cornerRadius={4}
            activeIndex={active}
            activeShape={ActiveShape}
            onMouseEnter={(_, i) => setActive(i)}
          >
            {data.map((_, i) => (
              <Cell
                key={i}
                fill={`url(#pie-grad-${i % colors.length})`}
                style={{ filter: active === i ? undefined : "url(#pie-glow)" }}
              />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
      {activeItem && (
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center px-4 text-center">
          <p className="max-w-[10rem] text-2xs font-semibold uppercase tracking-wider text-muted">
            {activeItem.name}
          </p>
          <p className="mt-0.5 font-mono text-base font-semibold text-white">
            {formatUsdCompact(activeItem.liquidity)}
          </p>
          <p className="text-xs text-pls-100">{activeItem.percentage.toFixed(1)}%</p>
        </div>
      )}
    </div>
  );
}

export default LiquidityPie;
